'use client';

import * as React from 'react';

import { cn } from '@workspace/ui/lib/utils';

import { billingConfig, billingConfigDisplayIntervals } from '../config';
import { PriceInterval } from '../schema';
import { PriceIntervalSelector } from './price-interval-selector';
import { PricingCard, type PricingTestimonial } from './pricing-card';
import { QuantitySelector } from './quantity-selector';

export type PricingTableProps = React.HtmlHTMLAttributes<HTMLDivElement> & {
  pending?: boolean;
  onUpgrade?: (productId: string, planId: string, quantity?: number) => void;
  currentProductId?: string;
  /** Forwarded to PricingCard — suppresses the trial CTA/copy when false. */
  trialEligible?: boolean;
  /**
   * Optional display-only override of which product is marked "Recommended".
   * When provided, exactly that product is recommended and all others are not.
   * When omitted, each product's config `recommended` flag is used.
   */
  recommendedProductId?: string;
  /** Shown on the recommended subscription card only. */
  featuredTestimonial?: PricingTestimonial;
  /** Analytics-only hook fired when any plan CTA is clicked. */
  onPlanCtaClick?: (
    productId: string,
    planId: string,
    interval: PriceInterval
  ) => void;
  /**
   * Hides the One-Time (Credit Packs) tab. Used while an org is trialing —
   * pack purchases are blocked server-side during the trial and the trial
   * caps are absolute, so the tab would only dead-end.
   */
  hideOneTimeProducts?: boolean;
};

export function PricingTable({
  pending,
  onUpgrade,
  currentProductId,
  trialEligible,
  recommendedProductId,
  featuredTestimonial,
  onPlanCtaClick,
  hideOneTimeProducts,
  className,
  ...other
}: PricingTableProps): React.JSX.Element {
  const displayIntervals = hideOneTimeProducts
    ? billingConfigDisplayIntervals.filter((i) => i !== PriceInterval.OneTime)
    : billingConfigDisplayIntervals;
  const [selectedInterval, setSelectedInterval] = React.useState<PriceInterval>(
    displayIntervals.includes(PriceInterval.Year)
      ? PriceInterval.Year
      : displayIntervals[0]
  );
  const [creditPackQuantity, setCreditPackQuantity] = React.useState<number>(1);

  const visibleProducts = billingConfig.products.filter((product) => {
    if (product.hidden) return false;
    return product.plans.some((plan) =>
      plan.displayIntervals.includes(selectedInterval)
    );
  });

  const isOneTime =
    selectedInterval === PriceInterval.OneTime && !hideOneTimeProducts;

  const creditPackProducts = visibleProducts.filter((p) =>
    p.id.startsWith('credit-pack-')
  );
  const subscriptionProducts = visibleProducts.filter(
    (p) => !p.id.startsWith('credit-pack-')
  );

  const subscriptionCount = subscriptionProducts.length;

  return (
    <div
      className={cn('flex flex-col space-y-6 xl:space-y-8', className)}
      {...other}
    >
      {displayIntervals.length > 1 && (
        <PriceIntervalSelector
          interval={selectedInterval}
          onIntervalChange={setSelectedInterval}
          intervals={displayIntervals}
        />
      )}

      {isOneTime ? (
        /* Credit Packs tab */
        creditPackProducts.length > 0 && (
          <div className="space-y-6">
            <div className="flex flex-col items-center gap-4">
              <h3 className="text-lg font-semibold">Credit Packs</h3>
              <QuantitySelector
                value={creditPackQuantity}
                onChange={setCreditPackQuantity}
              />
            </div>
            <div
              className={cn(
                'grid gap-4 w-full pt-3 mx-auto',
                creditPackProducts.length === 1 &&
                  'grid-cols-1 max-w-md',
                creditPackProducts.length === 2 &&
                  'grid-cols-1 sm:grid-cols-2 max-w-2xl',
                creditPackProducts.length >= 3 &&
                  'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl'
              )}
            >
              {creditPackProducts.map((product) => {
                const plan = product.plans.find((p) =>
                  p.displayIntervals.includes(selectedInterval)
                );
                if (!plan) return null;
                return (
                  <PricingCard
                    key={plan.id}
                    pending={pending}
                    product={product}
                    plan={plan}
                    selectedInterval={selectedInterval}
                    isCurrent={currentProductId === product.id}
                    quantity={creditPackQuantity}
                    onUpgrade={onUpgrade}
                    onPlanCtaClick={onPlanCtaClick}
                  />
                );
              })}
            </div>
          </div>
        )
      ) : (
        /* Subscription products (Month/Year tab) */
        <div
          className={cn(
            'grid gap-6 w-full items-stretch px-1 pt-5 mx-auto',
            subscriptionCount === 1 && 'grid-cols-1 max-w-md',
            subscriptionCount === 2 &&
              'grid-cols-1 sm:grid-cols-2 max-w-2xl',
            subscriptionCount === 3 &&
              'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl',
            subscriptionCount >= 4 &&
              'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
          )}
        >
          {subscriptionProducts.map((product) => {
            const plan = product.plans.find((p) =>
              p.displayIntervals.includes(selectedInterval)
            );
            if (!plan) return null;
            const isRecommended =
              recommendedProductId !== undefined
                ? product.id === recommendedProductId
                : Boolean(product.recommended);
            const displayProduct =
              recommendedProductId !== undefined
                ? { ...product, recommended: isRecommended }
                : product;
            return (
              <PricingCard
                key={plan.id}
                pending={pending}
                product={displayProduct}
                plan={plan}
                selectedInterval={selectedInterval}
                isCurrent={currentProductId === displayProduct.id}
                trialEligible={trialEligible}
                featuredTestimonial={
                  isRecommended ? featuredTestimonial : undefined
                }
                onUpgrade={onUpgrade}
                onPlanCtaClick={onPlanCtaClick}
                className={cn(isRecommended && 'order-first sm:order-none')}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
